import React, {useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import {getCoreRowModel, useReactTable} from "@tanstack/react-table";

import useAccessRulesEditState from "../useAccessRulesEditState";
import useDataTable from "../DataTable/useDataTable";
import DataTable from "../DataTable";
import PermissionCheckbox from "./PermissionCheckbox";
import PermissionHeader from "./PermissionHeader";
import {useAclQuery, useAclMutation} from "./query";

import ColumnHeader from "./ColumnHeader";


// AclTable is a table that shows the permissions of a group or user on a given resource and vice versa.
// The row can be either a user, a group or a resource. This entity is referred as
// principalType is either "group" or "user"
// principalId is the id of the group or user
const AclTable = (props) => {
    const {
        principalId,
        principalType,
        resourcesType,
        resourceId,
        permissions,
        entityHeader,
        renderEntityCol,
        multipleCheck,
        title,
        entitySortFieldName,
        isPermissionDisabled = (entity, permission) => false,
        dataTableProps={}
    } = props;

    if (principalId !== undefined && resourceId !== undefined) {
        throw new Error("principalId and resourceId cannot be both defined");
    }
    if (principalId === undefined && resourceId === undefined) {
        throw new Error("principalId or resourceId must be defined");
    }
    // type of the entity associated with each row
    const entityType = principalId !== undefined ? "principal" : "resource";

    const ruleEditState = useAccessRulesEditState();
    const {
        ruleState,
        changesCount,
        cancelChanges,
        reset,
        hasAccessRule,
        toggleAccessRule,
        setAccessRule,
    } = ruleEditState;

    const {
        fetchDataOptions,
        pagination,
        setPagination,
        sorting,
        onGlobalFilterChange,
        onSortingChange
    } = useDataTable();

    const aclQueryFilter = {
        ...fetchDataOptions,
        orderBy: sorting
            .map(({id, desc}) => id === "entity" ? ({id: entitySortFieldName, desc}) : ({id, desc}))
            .map(({id, desc}) => `${desc ? "+" : "-"}${id}`).join(",")
    }

    const aclQuery = useAclQuery(principalType, principalId, resourcesType, resourceId, aclQueryFilter)
    const aclMutation = useAclMutation(principalType, principalId, resourcesType, reset)
    const pageCount = aclQuery.data?.page_count || 0;
    const rows = aclQuery.data?.rows;
    const rowsLength = rows ? rows.length : 0;

    const getAccessRule = (entityId, permission) => {
        if (entityType === "principal") {
            switch (principalType) {
                case "user":
                    return {userId: principalId, resourceId: entityId, permission, groupId: null};
                case "group":
                    return {userId: null, resourceId: entityId, permission, groupId: principalId};
            }
        }else if(entityType === "resource"){
            switch (principalType) {
                case "user":
                    return {userId: entityId, resourceId: resourceId, permission, groupId: null};
                case "group":
                    return {userId: null, resourceId: resourceId, permission, groupId: entityId};
            }
        }else {
            throw new Error(`Invalid entity: ${entityType}, must be "principal" or "resource"`);
        }
    }

    // All the editable rules, i.e. cross product of rows and permissions
    // If a permission is disabled for a given row, the corresponding
    // rule is not included
    const getEditableRules = () => {
        if (!rows) {
            return [];
        }
        if(entityType === "principal") {
            return rows.flatMap(r => permissions
                .filter(p => !isPermissionDisabled(r.entity, p))
                .map(p => getAccessRule(r.entity.id, p)));
        }
        if(entityType === "resource") {
            // do not filter out the permissions that are disabled because
            // we assume that the list of permissions contains only the
            // permissions that are enabled for the given resource
            return rows.flatMap(r => permissions
                .filter(p => !isPermissionDisabled(r.entity, p))
                .map(p => getAccessRule(r.entity.id, p))
            );
        }
        throw new Error(`Invalid entityType: ${entityType}, must be "principal" or "resource"`);
    }

    const editableRules = getEditableRules();

    const hasGroupRule = (acl, permission) => {
        if (principalType === "group") {
            return false;
        }
        return acl.some(r => r.permission === permission && r.groupId !== null);
    }

    useEffect(() => {
        // Do not set the access rules if the query is still fetching because it might contain
        // not fresh data
        if (aclQuery.data && !aclQuery.isFetching) {
            aclQuery.data.rows.forEach(r => {
                // TODO call setAccessRule only once passing all the rules
                r.accessControlRules.forEach(accessRule => setAccessRule(accessRule));
            })
        }
    }, [aclQuery.data, aclQuery.isFetching])

    const handleApplyChanges = async () => {
        await aclMutation.mutateAsync({
            additions: ruleState.addedRules,
            removals: ruleState.removedRules}
        );
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: 'entity',
                cell: props => renderEntityCol(props?.row?.original?.entity),
                header: () => <span>{entityHeader}</span>,
                enableSorting: true,
                footer: props => props.column.id,
            },
            ...permissions.map(permission => ({
                accessorKey: permission,
                cell: props => <PermissionCheckbox accessRule={getAccessRule(props?.row?.original?.entity?.id, permission)}
                                                   hasAccessRule={hasAccessRule}
                                                   onToggle={toggleAccessRule}
                                                   alwaysChecked={hasGroupRule(props?.row?.original?.accessControlRules, permission)}
                                                   disabled={isPermissionDisabled(props?.row?.original?.entity, permission)}/>,
                header: () => <ColumnHeader permission={permission}
                                            accessRules={editableRules.filter(r => r.permission === permission)}
                                            resourcesType={resourcesType}
                                            multipleCheck={multipleCheck && rowsLength > 1}
                                            ruleEditState={ruleEditState}/>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "12rem",
                    align: "center"
                }
            }))
        ],
        [ruleState, aclQuery.data?.rows]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const table = useReactTable({
        data: rows ?? [],
        columns,
        pageCount: pageCount ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
    })

    return <>
        <PermissionHeader title={title}
                          ruleState={ruleState}
                          changesCount={changesCount}
                          onCancel={cancelChanges}
                          onApply={handleApplyChanges}/>
        <DataTable table={table} canSearch {...dataTableProps}/>
    </>
}

// prop types
AclTable.propTypes = {
    principalId: PropTypes.number,
    resourceId: PropTypes.number,
    principalType: PropTypes.oneOf(["user", "group"]).isRequired,
    resourcesType: PropTypes.oneOf(["station", "workgroup"]).isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    entityHeader: PropTypes.string.isRequired,
    renderEntityCol: PropTypes.func.isRequired,
    multipleCheck: PropTypes.bool,
    entitySortFieldName: PropTypes.string,
    isPermissionDisabled: PropTypes.func,  // a function (entity, permission) => bool that returns true if the pair (entity, permission) should be disabled
    dataTableProps: PropTypes.object, // props to pass to the DataTable component
}

export default AclTable;